import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
} from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import {
  primaryDark,
  accentColor,
  buttonGradient,
  buttonGradientHover,
} from "../../../constants/consts";

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "box-shadow 0.25s ease, background-color 0.25s ease",
    "&:hover": { backgroundColor: "rgba(4,87,129,0.02)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(4,87,129,0.10)" },
  },
};

/* ISO → yyyy-MM-dd (input type=date) */
const toDateInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  } catch {
    return "";
  }
};

export default function EduEditDialog({
  open,
  edu,
  sunucuKoduList = [],
  personelList = [],
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    title: "",
    sunucuKodu: "",
    supporter: "",
    eduDate: "",
    duration: "",
    desc: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && edu) {
      setForm({
        title: edu.title ?? "",
        sunucuKodu: edu.sunucuKodu ?? "",
        supporter: edu.supporter ?? "",
        eduDate: toDateInput(edu.eduDate),
        duration: edu.duration ?? "",
        desc: edu.desc ?? "",
      });
      setError("");
      setSaving(false);
    }
  }, [open, edu]);

  const handleChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handleSave = async () => {
    if (
      !form.title.trim() ||
      !form.sunucuKodu ||
      !form.supporter ||
      !form.eduDate ||
      !form.duration ||
      !form.desc.trim()
    ) {
      setError("Tüm alanlar zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await adminService.editEdu(edu.id, {
        title: form.title.trim(),
        sunucuKodu: form.sunucuKodu,
        supporter: form.supporter,
        eduDate: form.eduDate,
        duration: String(form.duration),
        desc: form.desc.trim(),
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      setError("Eğitim güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          animation: `${popIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1) both`,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.75,
          background: `linear-gradient(135deg, ${primaryDark} 0%, ${accentColor} 100%)`,
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditOutlined sx={{ fontSize: 20 }} />
          Eğitimi Düzenle
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            label="Konu *"
            value={form.title}
            onChange={handleChange("title")}
            fullWidth
            size="small"
            sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
          />

          <Autocomplete
            size="small"
            options={sunucuKoduList}
            value={form.sunucuKodu || null}
            onChange={(_, v) =>
              setForm((prev) => ({ ...prev, sunucuKodu: v || "" }))
            }
            filterOptions={(opts, { inputValue }) =>
              opts.filter((o) => normalize(o).includes(normalize(inputValue)))
            }
            noOptionsText="Sunucu bulunamadı"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sunucu Kodu *"
                placeholder="Yaz veya seç"
                sx={inputSx}
              />
            )}
          />

          <Autocomplete
            size="small"
            options={personelList}
            value={form.supporter || null}
            onChange={(_, v) =>
              setForm((prev) => ({ ...prev, supporter: v || "" }))
            }
            filterOptions={(opts, { inputValue }) =>
              opts.filter((o) => normalize(o).includes(normalize(inputValue)))
            }
            noOptionsText="Personel bulunamadı"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Destek Personeli *"
                placeholder="Yaz veya seç"
                sx={inputSx}
              />
            )}
          />

          <TextField
            label="Eğitim Tarihi *"
            type="date"
            value={form.eduDate}
            onChange={handleChange("eduDate")}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />

          <TextField
            label="Süre (saat) *"
            type="number"
            value={form.duration}
            onChange={handleChange("duration")}
            fullWidth
            size="small"
            inputProps={{ min: 0, step: 1 }}
            sx={inputSx}
          />

          <TextField
            label="Açıklama *"
            value={form.desc}
            onChange={handleChange("desc")}
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            size="small"
            sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
          />
        </Box>

        {error && (
          <Typography
            sx={{ mt: 2, color: "#B91C1C", fontSize: 12.5, fontWeight: 600 }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            background: buttonGradient,
            "&:hover": { background: buttonGradientHover },
            "&:disabled": { background: "#c7c7c7" },
          }}
        >
          {saving ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Kaydet"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
